/**
 * Imputación › Equipo (admins only).
 *
 * Answers "who is behind, and by how much" for one month at a glance, says
 * whether each figure was verified with the person's own Redmine login, and
 * drills down into that person's grid (read-only). Everything here is GET:
 * nobody logs time for someone else from this tab.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { api } from '../../../services/api'
import type { ImputacionLevel, TeamImputacionStatus, TeamScope, UserImputationSummary } from '../../../types/imputacion'
import { Spinner, LoadingContainer } from '../../../components/shared/StyledComponents'
import { ImputacionPanel } from '../components'
import {
    ageText,
    fmtDayMonth,
    fmtHours,
    monthName,
    monthTitle,
    pendingListText,
    pendingUsers,
    reminderText,
} from './reminder'

type Scope = TeamScope

const SCOPES: { key: Scope; label: string }[] = [
    { key: 'team', label: 'Equipo' },
    { key: 'interns', label: 'Prácticas' },
    { key: 'all', label: 'Todos' },
]

// Levels without a reliable figure: shown greyed and grouped at the bottom.
const GREY_LEVELS: ImputacionLevel[] = ['sin_jornada', 'sin_mapear', 'sin_datos']

const HIDDEN_PROJECTS_LABEL = 'Otros proyectos (no visibles para el Manager)'

/** Month-end close: on days 1-5 the month that still needs chasing is the previous one. */
const CLOSE_DAYS = 5

type YM = { year: number; month: number }

/**
 * Today's calendar date in Europe/Madrid: the server decides "today", the
 * editable window and the month-end close in that zone, so the selector must
 * agree with it even when the admin's browser runs on another clock.
 */
const madridToday = (now: Date = new Date()): YM & { day: number } => {
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Europe/Madrid',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        }).formatToParts(now)
        const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
        const year = get('year')
        const month = get('month')
        const day = get('day')
        if (year && month && day) return { year, month, day }
    } catch {
        // Intl without time-zone data: fall back to the browser's own date.
    }
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }
}

const currentYM = (): YM => {
    const { year, month } = madridToday()
    return { year, month }
}

const shiftMonth = ({ year, month }: YM, delta: number): YM => {
    const idx = year * 12 + (month - 1) + delta
    return { year: Math.floor(idx / 12), month: (idx % 12) + 1 }
}

const isGrey = (level: ImputacionLevel) => GREY_LEVELS.includes(level)

/** Sort: people with a figure first, most hours missing on top; grey states last, by name. */
export const sortTeamUsers = (users: UserImputationSummary[]): UserImputationSummary[] =>
    [...users].sort((a, b) => {
        const ga = isGrey(a.totals.level) ? 1 : 0
        const gb = isGrey(b.totals.level) ? 1 : 0
        if (ga !== gb) return ga - gb
        if (!ga && b.totals.missing_to_date !== a.totals.missing_to_date) {
            return b.totals.missing_to_date - a.totals.missing_to_date
        }
        return a.user_name.localeCompare(b.user_name, 'es')
    })

type LevelView = { icon: string; text: string; tone: 'ok' | 'warn' | 'danger' | 'maybe' | 'grey'; title?: string }

const levelView = (u: UserImputationSummary): LevelView => {
    switch (u.totals.level) {
        case 'al_dia':
            return { icon: '✓', text: 'Al día', tone: 'ok' }
        case 'retrasado':
            return { icon: '!', text: 'Retrasado', tone: 'warn' }
        case 'critico':
            return { icon: '‼', text: 'Crítico', tone: 'danger' }
        case 'posible_retraso':
            return {
                icon: '?',
                text: 'Posible retraso',
                tone: 'maybe',
                title: 'Puede tener horas en proyectos que el Manager no ve',
            }
        case 'sin_jornada':
            return { icon: '', text: 'Jornada sin configurar', tone: 'grey' }
        case 'sin_mapear':
            return {
                icon: '',
                text: u.shared_redmine_user ? 'Usuario Redmine compartido' : 'Sin usuario Redmine',
                tone: 'grey',
            }
        default:
            return { icon: '', text: 'Sin datos', tone: 'grey' }
    }
}

/** Grey states have no trustworthy "Faltan" figure (unmapped, shared id or no Redmine data). */
const hasMissingFigure = (u: UserImputationSummary) =>
    u.totals.level !== 'sin_datos' && u.totals.level !== 'sin_mapear'

const lastLoggedText = (u: UserImputationSummary): string => {
    const last = u.last_logged_date ?? u.totals.last_logged_date
    if (!last) return 'Nada este mes'
    const n = u.working_days_since_last
    if (n === null || n === undefined || n <= 0) return fmtDayMonth(last)
    return `${fmtDayMonth(last)} · hace ${n} ${n === 1 ? 'día laborable' : 'días laborables'}`
}

const pct = (v: number | null | undefined) => (v === null || v === undefined ? null : Math.max(0, Math.min(100, v)))

/**
 * Copies text with the async Clipboard API, falling back to a hidden
 * textarea + execCommand where the API is missing or refused (plain http,
 * older browsers, permissions).
 */
const copyText = async (text: string): Promise<boolean> => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // fall through to the textarea fallback
    }
    try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        const ok = document.execCommand?.('copy') ?? false
        document.body.removeChild(ta)
        return ok
    } catch {
        return false
    }
}

export function TeamImputacionTab({ initialYear, initialMonth }: { initialYear?: number; initialMonth?: number }) {
    // Decided once on mount: a deep link wins; otherwise days 1-5 open the
    // previous month to chase the month-end close.
    const [opening] = useState(() => {
        const today = madridToday()
        const cur = { year: today.year, month: today.month }
        if (initialYear && initialMonth) return { ym: { year: initialYear, month: initialMonth }, closing: false }
        if (today.day <= CLOSE_DAYS) return { ym: shiftMonth(cur, -1), closing: true }
        return { ym: cur, closing: false }
    })
    const [ym, setYm] = useState<YM>(opening.ym)
    const [scope, setScope] = useState<Scope>('team')
    const [data, setData] = useState<TeamImputacionStatus | null>(null)
    // Which month and scope `data` belongs to. While a new month or scope loads
    // the previous table stays on screen (dimmed); if that load fails, the old
    // table must not be passed off as the month or group now selected.
    const [dataKey, setDataKey] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [expanded, setExpanded] = useState<Set<number>>(new Set())
    const [viewing, setViewing] = useState<UserImputationSummary | null>(null)
    const [toast, setToast] = useState<string | null>(null)
    const requestSeq = useRef(0)
    const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const cur = currentYM()
    const viewKey = `${ym.year}-${ym.month}-${scope}`
    const fresh = data !== null && dataKey === viewKey
    const canGoForward = ym.year * 12 + ym.month < cur.year * 12 + cur.month

    const load = useCallback(
        async (refresh = false) => {
            // Only the latest request may write state: switching month or scope
            // quickly must not let an older, slower answer win.
            const seq = ++requestSeq.current
            setLoading(true)
            setError(false)
            try {
                const res = await api.imputacion.getTeam(ym.year, ym.month, scope, refresh)
                if (seq !== requestSeq.current) return
                setData(res.data)
                setDataKey(`${ym.year}-${ym.month}-${scope}`)
            } catch {
                if (seq !== requestSeq.current) return
                setError(true)
            } finally {
                if (seq === requestSeq.current) setLoading(false)
            }
        },
        [ym.year, ym.month, scope],
    )

    useEffect(() => {
        void load(false)
    }, [load])

    useEffect(
        () => () => {
            if (toastTimer.current) clearTimeout(toastTimer.current)
        },
        [],
    )

    useEffect(() => {
        if (!viewing) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setViewing(null)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [viewing])

    const showToast = (text: string) => {
        setToast(text)
        if (toastTimer.current) clearTimeout(toastTimer.current)
        toastTimer.current = setTimeout(() => setToast(null), 2500)
    }

    const copy = async (text: string) => {
        showToast((await copyText(text)) ? 'Copiado' : 'No se pudo copiar')
    }

    const toggle = (uid: number) =>
        setExpanded((prev) => {
            const next = new Set(prev)
            if (next.has(uid)) next.delete(uid)
            else next.add(uid)
            return next
        })

    // Copy texts and the drill-down use the month the table was built for, not
    // the selector, so a click while another month loads never mixes the two.
    const dataYear = data?.year ?? ym.year
    const dataMonth = data?.month ?? ym.month
    const users = useMemo(() => sortTeamUsers(data?.users ?? []), [data])
    const pending = useMemo(() => pendingUsers(data?.users ?? []), [data])
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    // The table may come from the server cache (cached_at is when it was built); say how old it is.
    const dataAge = ageText(data?.cached_at ?? null)
    const curName = monthName(cur.month)
    const showCloseNote = opening.closing && ym.year === opening.ym.year && ym.month === opening.ym.month

    return (
        <Wrapper>
            <Controls>
                <MonthSwitch>
                    <NavButton
                        type="button"
                        aria-label="Mes anterior"
                        onClick={() => setYm((p) => shiftMonth(p, -1))}
                    >
                        ‹
                    </NavButton>
                    <MonthLabel data-testid="team-month-label">
                        {monthTitle(ym.month)} {ym.year}
                    </MonthLabel>
                    <NavButton
                        type="button"
                        aria-label="Mes siguiente"
                        disabled={!canGoForward}
                        onClick={() => setYm((p) => shiftMonth(p, 1))}
                    >
                        ›
                    </NavButton>
                </MonthSwitch>
                <Chips role="group" aria-label="Personas">
                    {SCOPES.map((s) => (
                        <Chip
                            key={s.key}
                            type="button"
                            $active={scope === s.key}
                            aria-pressed={scope === s.key}
                            onClick={() => setScope(s.key)}
                        >
                            {s.label}
                        </Chip>
                    ))}
                </Chips>
                <Button type="button" onClick={() => void load(true)} disabled={loading}>
                    🔄 Actualizar
                </Button>
                {dataAge && <Muted>Datos de hace {dataAge}</Muted>}
                <Spacer />
                <SecondaryButton
                    type="button"
                    disabled={!fresh || !pending.length}
                    onClick={() => void copy(pendingListText(data?.users ?? [], dataYear, dataMonth, origin))}
                >
                    📋 Copiar lista de pendientes
                </SecondaryButton>
            </Controls>

            {showCloseNote && (
                <Note>
                    Mostrando {monthName(ym.month)} (cierre de mes) ·{' '}
                    <LinkButton type="button" onClick={() => setYm(cur)}>
                        Ver {curName} →
                    </LinkButton>
                </Note>
            )}

            {fresh && data && !data.redmine_available && (
                <Banner role="alert">Redmine no disponible: {data.redmine_message ?? 'sin respuesta'}</Banner>
            )}

            {loading && !data ? (
                <LoadingContainer>
                    <Spinner />
                </LoadingContainer>
            ) : error && !fresh ? (
                <ErrorBox role="alert">
                    No se pudo cargar el estado del equipo.{' '}
                    <LinkButton type="button" onClick={() => void load(false)}>
                        Reintentar
                    </LinkButton>
                </ErrorBox>
            ) : data ? (
                <>
                    {error && <ErrorBox role="alert">No se pudo actualizar; se muestran los últimos datos.</ErrorBox>}
                    <Kpis>
                        <Kpi>
                            <KpiValue>
                                {data.team.al_dia}/{data.team.people}
                            </KpiValue>
                            <KpiLabel>Personas al día</KpiLabel>
                        </Kpi>
                        <Kpi>
                            <KpiValue>{fmtHours(data.team.missing_to_date)} h</KpiValue>
                            <KpiLabel>Horas pendientes</KpiLabel>
                        </Kpi>
                        <Kpi>
                            <KpiValue>{data.team.unverified}</KpiValue>
                            <KpiLabel>Sin verificar</KpiLabel>
                        </Kpi>
                    </Kpis>

                    {users.length === 0 ? (
                        <Muted>No hay personas en este grupo.</Muted>
                    ) : (
                        <TableWrapper $busy={loading}>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>Persona</Th>
                                        <Th>Faltan</Th>
                                        <Th>Último día imputado</Th>
                                        <Th>Estado</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((u) => {
                                        const open = expanded.has(u.user_id)
                                        const lv = levelView(u)
                                        const coverage = pct(u.totals.coverage_pct)
                                        const verifiedAge = ageText(u.own_snapshot_at)
                                        return (
                                            <Fragment key={u.user_id}>
                                                <Row data-testid={`team-row-${u.user_id}`} $grey={lv.tone === 'grey'}>
                                                    <Td>
                                                        <ExpandButton
                                                            type="button"
                                                            aria-expanded={open}
                                                            onClick={() => toggle(u.user_id)}
                                                        >
                                                            <span aria-hidden="true">{open ? '▾' : '▸'}</span>{' '}
                                                            {u.user_name}
                                                        </ExpandButton>
                                                        <Badges>
                                                            {u.role === 'admin' && <Badge>Admin</Badge>}
                                                            {(u.role === 'practices' || u.user_type === 'intern') && (
                                                                <Badge>Prácticas</Badge>
                                                            )}
                                                            {u.verified ? (
                                                                <Badge $tone="ok">
                                                                    ✓ verificado con su login
                                                                    {verifiedAge ? ` · hace ${verifiedAge}` : ''}
                                                                </Badge>
                                                            ) : (
                                                                <Badge $tone="warn">⚠ sin verificar</Badge>
                                                            )}
                                                        </Badges>
                                                    </Td>
                                                    <Td>
                                                        {hasMissingFigure(u) ? (
                                                            <>
                                                                <strong>{fmtHours(u.totals.missing_to_date)} h</strong>
                                                                {coverage !== null && (
                                                                    <Progress
                                                                        role="progressbar"
                                                                        aria-valuenow={Math.round(coverage)}
                                                                        aria-valuemin={0}
                                                                        aria-valuemax={100}
                                                                        aria-label="Imputado sobre lo esperado"
                                                                        title={`${Math.round(coverage)} % imputado`}
                                                                    >
                                                                        <ProgressFill style={{ width: `${coverage}%` }} />
                                                                    </Progress>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Muted>—</Muted>
                                                        )}
                                                    </Td>
                                                    <Td>{lastLoggedText(u)}</Td>
                                                    <Td>
                                                        <Status $tone={lv.tone} title={lv.title}>
                                                            {lv.icon && <span aria-hidden="true">{lv.icon} </span>}
                                                            {lv.text}
                                                        </Status>
                                                    </Td>
                                                </Row>
                                                {open && (
                                                    <tr>
                                                        <DetailCell colSpan={4}>
                                                            <UserDetail
                                                                user={u}
                                                                onView={() => setViewing(u)}
                                                                onRemind={(text) => void copy(text)}
                                                                reminder={reminderText(u, dataYear, dataMonth, origin)}
                                                            />
                                                        </DetailCell>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        )
                                    })}
                                </tbody>
                            </Table>
                        </TableWrapper>
                    )}
                </>
            ) : null}

            {viewing && (
                <Overlay onClick={() => setViewing(null)}>
                    <Modal
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="team-imputacion-modal-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ModalHeader>
                            <ModalTitle id="team-imputacion-modal-title">
                                Imputación de {viewing.user_name} — según los datos que ve el Manager
                                {dataAge ? ` (actualizado hace ${dataAge})` : ''}
                            </ModalTitle>
                            <CloseButton type="button" aria-label="Cerrar" onClick={() => setViewing(null)}>
                                &times;
                            </CloseButton>
                        </ModalHeader>
                        <ModalBody>
                            <ImputacionPanel
                                userId={viewing.user_id}
                                readOnly
                                initialYear={dataYear}
                                initialMonth={dataMonth}
                            />
                        </ModalBody>
                    </Modal>
                </Overlay>
            )}

            {toast && <Toast role="status">{toast}</Toast>}
        </Wrapper>
    )
}

function UserDetail({
    user,
    reminder,
    onView,
    onRemind,
}: {
    user: UserImputationSummary
    reminder: string | null
    onView: () => void
    onRemind: (text: string) => void
}) {
    const t = user.totals
    const coverage = pct(t.coverage_pct)
    const missingDays = t.days_without_entries ?? []
    return (
        <Detail>
            {!user.mapped && !user.shared_redmine_user && (
                <Muted as="p">Se vinculará solo cuando abra Imputación con su usuario.</Muted>
            )}
            {user.shared_redmine_user && (
                <Muted as="p">
                    Comparte usuario de Redmine con otra persona: sus horas no se cuentan aquí para no duplicarlas.
                </Muted>
            )}
            <Facts>
                <Fact>
                    <FactLabel>Esperado hasta ayer</FactLabel>
                    <FactValue>{fmtHours(t.expected_to_date)} h</FactValue>
                </Fact>
                <Fact>
                    <FactLabel>Imputado</FactLabel>
                    <FactValue>{fmtHours(t.logged_to_date)} h</FactValue>
                </Fact>
                <Fact>
                    <FactLabel>%</FactLabel>
                    <FactValue>{coverage === null ? '—' : `${Math.round(coverage)} %`}</FactValue>
                </Fact>
                <Fact>
                    <FactLabel>Días sin imputar</FactLabel>
                    <FactValue>{missingDays.length ? missingDays.map(fmtDayMonth).join(', ') : '—'}</FactValue>
                </Fact>
                <Fact>
                    <FactLabel>Previsión mes</FactLabel>
                    <FactValue>
                        {fmtHours(t.forecast_month)} h de {fmtHours(t.expected_month)} h
                    </FactValue>
                </Fact>
                <Fact>
                    <FactLabel>Imputado desde el Manager</FactLabel>
                    <FactValue>{fmtHours(user.via_manager_hours)} h</FactValue>
                </Fact>
            </Facts>

            {user.projects.length > 0 && (
                <ProjectTable>
                    <thead>
                        <tr>
                            <Th>Proyecto</Th>
                            <Th>Plan</Th>
                            <Th>Imputado</Th>
                            <Th>Previsión</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {user.projects.map((p) => (
                            <tr key={p.key}>
                                {/* Hidden projects never carry a name: the reader accounts cannot see them. */}
                                <Td>{p.kind === 'hidden' ? HIDDEN_PROJECTS_LABEL : p.label}</Td>
                                <Td>{p.kind === 'hidden' ? '—' : `${fmtHours(p.summary.plan_month)} h`}</Td>
                                <Td>{fmtHours(p.summary.logged_month)} h</Td>
                                <Td>{fmtHours(p.summary.forecast_month)} h</Td>
                            </tr>
                        ))}
                    </tbody>
                </ProjectTable>
            )}

            <Actions>
                <Button type="button" onClick={onView}>
                    Ver su tabla
                </Button>
                {reminder && (
                    <SecondaryButton type="button" onClick={() => onRemind(reminder)}>
                        📋 Copiar recordatorio
                    </SecondaryButton>
                )}
            </Actions>
        </Detail>
    )
}

// ── styles (tokens follow MyBillingTable / TeamBillingSection) ──────────────

type Tone = LevelView['tone']

const toneColor = (tone: Tone | undefined) => {
    switch (tone) {
        case 'ok':
            return { fg: '#2E7D32', bg: '#E8F5E9' }
        case 'warn':
            return { fg: '#E65100', bg: '#FFF3E0' }
        case 'danger':
            return { fg: '#C62828', bg: '#FFEBEE' }
        case 'maybe':
            return { fg: '#6D4C41', bg: '#FFF8E1' }
        case 'grey':
            return { fg: '#616161', bg: '#F5F5F5' }
        default:
            return { fg: '#37474F', bg: '#ECEFF1' }
    }
}

const Wrapper = styled.div`
    position: relative;
`

const Controls = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
`

const Spacer = styled.div`
    flex: 1;
`

const MonthSwitch = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs};
`

const MonthLabel = styled.span`
    font-weight: 600;
    min-width: 140px;
    text-align: center;
    color: ${({ theme }) => theme.colors.primary};
`

const NavButton = styled.button`
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: white;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.fontSizes.md};
    cursor: pointer;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`

const Chips = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing.xs};
`

const Chip = styled.button<{ $active: boolean }>`
    border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
    background: ${({ theme, $active }) => ($active ? theme.colors.primary : 'white')};
    color: ${({ $active }) => ($active ? 'white' : 'inherit')};
    border-radius: 999px;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    cursor: pointer;
`

const Button = styled.button`
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: 600;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border: none;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const SecondaryButton = styled(Button)`
    background: white;
    color: ${({ theme }) => theme.colors.primary};
    border: 1px solid ${({ theme }) => theme.colors.primary};
`

const LinkButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
    font-size: inherit;
`

const Muted = styled.span`
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: ${({ theme }) => theme.fontSizes.sm};
`

const Note = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.sm};
    color: ${({ theme }) => theme.colors.textMuted};
    margin-bottom: ${({ theme }) => theme.spacing.md};
`

const Banner = styled.div`
    background: #fff3e0;
    border-left: 4px solid ${({ theme }) => theme.colors.warning};
    padding: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
`

const ErrorBox = styled(Banner)`
    background: #ffebee;
    border-left-color: ${({ theme }) => theme.colors.danger};
`

const Kpis = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const Kpi = styled.div`
    background: linear-gradient(135deg, #e3f2fd, #fff);
    border: 1px solid #90caf9;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing.md};
    text-align: center;
`

const KpiValue = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.xl};
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
`

const KpiLabel = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.xs};
    color: ${({ theme }) => theme.colors.textMuted};
    margin-top: ${({ theme }) => theme.spacing.xs};
`

const TableWrapper = styled.div<{ $busy: boolean }>`
    overflow-x: auto;
    max-width: 100%;
    opacity: ${({ $busy }) => ($busy ? 0.6 : 1)};
    transition: opacity 0.2s ease;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    min-width: 640px;
`

const Th = styled.th`
    padding: ${({ theme }) => theme.spacing.sm};
    text-align: left;
    font-weight: 600;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.secondary};
    white-space: nowrap;
`

const Td = styled.td`
    padding: ${({ theme }) => theme.spacing.sm};
    border: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
`

const Row = styled.tr<{ $grey: boolean }>`
    color: ${({ $grey, theme }) => ($grey ? theme.colors.textMuted : 'inherit')};
`

const ExpandButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: 600;
    color: inherit;
    cursor: pointer;
    text-align: left;
`

const Badges = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.xs};
    margin-top: ${({ theme }) => theme.spacing.xs};
`

const Badge = styled.span<{ $tone?: Tone }>`
    font-size: ${({ theme }) => theme.fontSizes.xs};
    padding: 1px 6px;
    border-radius: 999px;
    color: ${({ $tone }) => toneColor($tone).fg};
    background: ${({ $tone }) => toneColor($tone).bg};
    white-space: nowrap;
`

const Status = styled.span<{ $tone: Tone }>`
    display: inline-block;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    color: ${({ $tone }) => toneColor($tone).fg};
    background: ${({ $tone }) => toneColor($tone).bg};
    white-space: nowrap;
`

const Progress = styled.div`
    margin-top: 4px;
    height: 6px;
    width: 100%;
    max-width: 140px;
    background: #eceff1;
    border-radius: 3px;
    overflow: hidden;
`

const ProgressFill = styled.div`
    height: 100%;
    background: ${({ theme }) => theme.colors.primary};
`

const DetailCell = styled.td`
    padding: ${({ theme }) => theme.spacing.md};
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: #fafbfc;
`

const Detail = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};

    p {
        margin: 0;
    }
`

const Facts = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: ${({ theme }) => theme.spacing.sm};
`

const Fact = styled.div``

const FactLabel = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.xs};
    color: ${({ theme }) => theme.colors.textMuted};
`

const FactValue = styled.div`
    font-weight: 600;
`

const ProjectTable = styled(Table)`
    min-width: 480px;
    max-width: 720px;
`

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.sm};
`

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`

const Modal = styled.div`
    background: white;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.xl};
    width: 96%;
    max-width: 1400px;
    max-height: 92vh;
    overflow-y: auto;
`

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
    position: sticky;
    top: 0;
    z-index: 1;
`

const ModalTitle = styled.h2`
    margin: 0;
    font-size: ${({ theme }) => theme.fontSizes.md};
`

const CloseButton = styled.button`
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
`

const ModalBody = styled.div`
    padding: ${({ theme }) => theme.spacing.lg};
`

const Toast = styled.div`
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    box-shadow: ${({ theme }) => theme.shadows.md};
    z-index: 1100;
`
